/**
 * Multi-store atomic transactions. The callback receives a writer bound to a
 * single IndexedDB `readwrite` transaction spanning every named store; the
 * transaction auto-commits when the callback resolves and rolls back if the
 * callback throws (we call `tx.abort()` explicitly, which is browser-atomic).
 *
 * Encoding/decoding is supplied per store by the caller. For the unencrypted
 * path the codec is synchronous, so the writer issues IDB requests without
 * yielding a microtask (which would deactivate the transaction). For the
 * encrypted path the codec is async; callers accept that an encrypted
 * multi-store write is best-effort sequential rather than zero-await.
 */

import { warn } from './error-codes'
import type { Codec } from './codec'
import { reqToPromise, txDone } from './idb'
import { OfflineStoreError } from './types'

/** Per-store writer handed to a {@link runTransaction} callback. */
export interface TxStore<T> {
	put(value: T): Promise<void>
	delete(key: IDBValidKey): Promise<void>
	get(key: IDBValidKey): Promise<T | undefined>
}

/** Map of table name → {@link TxStore}. */
export type TxScope = Record<string, TxStore<unknown>>

/**
 * Run `fn` inside one atomic `readwrite` transaction over `storeNames`.
 * Resolves with the callback's return value once the tx commits; rejects with
 * `OfflineStoreError(code="tx-aborted")` on any throw/abort, guaranteeing no
 * partial writes are visible.
 */
export async function runTransaction<R>(
	db: IDBDatabase,
	storeNames: ReadonlyArray<string>,
	codecOf: (table: string) => Codec,
	fn: (scope: TxScope) => Promise<R> | R,
): Promise<R> {
	const tx = db.transaction(storeNames as string[], 'readwrite')
	const done = txDone(tx)

	// IndexedDB auto-commits a transaction the moment its request queue drains.
	// A user callback that `await`s between writes crosses a microtask
	// checkpoint with no pending request, which would deactivate the tx and
	// throw `TransactionInactiveError` on the next op. We keep the tx alive by
	// continuously re-arming a cheap no-op request from inside its own success
	// callback until the user callback settles. This is the
	// browser-portable liveness technique (same shape Dexie/idb use).
	const keepAliveStore = tx.objectStore(storeNames[0] as string)
	let keepAliveStopped = false
	const pump = (): void => {
		if (keepAliveStopped) return
		let req: IDBRequest
		try {
			req = keepAliveStore.get(-1)
		} catch {
			return // tx already finishing — nothing to keep alive
		}
		req.onsuccess = () => pump()
		req.onerror = () => {
			/* keepalive errors are irrelevant */
		}
	}
	pump()
	const stopKeepAlive = () => {
		keepAliveStopped = true
	}

	const scope: TxScope = {}
	for (const name of storeNames) {
		const objStore = tx.objectStore(name)
		const codec = codecOf(name)
		scope[name] = {
			async put(value: unknown) {
				const encoded = await Promise.resolve(codec.encode(value))
				await reqToPromise(objStore.put(encoded))
			},
			async delete(key: IDBValidKey) {
				await reqToPromise(objStore.delete(key))
			},
			async get(key: IDBValidKey) {
				const raw = await reqToPromise(objStore.get(key))
				return raw === undefined ? undefined : await Promise.resolve(codec.decode(raw))
			},
		}
	}

	let result: R
	try {
		result = await fn(scope)
		stopKeepAlive()
	} catch (err) {
		stopKeepAlive()
		// The tx will (or already did) abort; its completion promise rejects.
		// Mark it handled so the abort does not surface as an unhandled
		// rejection — we are about to throw a structured error instead.
		done.catch(() => {})
		try {
			tx.abort()
		} catch {
			/* already aborting/committed */
		}
		warn({
			fn: 'runTransaction',
			code: 'WEB_OFFLINE_STORE_TX_ABORTED',
			message: `transaction over [${storeNames.join(', ')}] rolled back: ${
				err instanceof Error ? err.message : String(err)
			}`,
		})
		if (err instanceof OfflineStoreError) throw err
		throw new OfflineStoreError('tx-aborted', 'transaction callback threw; rolled back', {
			cause: err,
		})
	}

	await done
	return result
}
